import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import {
  FileBarChart,
  ChevronDown,
  ChevronRight,
  Loader2,
  FileSpreadsheet,
  FileDown,
  AlertCircle,
  X,
  BarChart3,
  PlayCircle,
  CheckCircle2,
  SkipForward,
  Clock,
  RefreshCw,
} from 'lucide-react';

import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import clsx from 'clsx';
import { periodDisplayName, type FinancialLineItem, type FinancialStatement, type Period, type Entity, type DrillDownItem, type NciReport, type NciReportRow, type NciPipelineResult, type NciPipelineStep } from '@/types';
import { reportsApi, periodsApi, entitiesApi, exportsApi, trialBalancesApi } from '@/api/client';
import { useAppStore } from '@/store';
import ConsolidationReviewPanel from '@/components/ConsolidationReviewPanel';
import SearchableSelect from '@/components/SearchableSelect';


// --- Format helpers ----------------------------------------------------------

function formatFinancialNumber(amount: number, showSign = false): string {
  if (amount === 0 && !showSign) return '\u2014';
  const abs = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  if (amount < 0) return `(${formatted})`;
  return formatted;
}

const DRILL_DOWN_COLORS = ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#7c3aed', '#a78bfa', '#c084fc', '#e879f9'];

// --- CSV Export helper -------------------------------------------------------

function exportToCSV(lineItems: FinancialLineItem[], title: string, periodName: string) {
  const headers = ['Account Code', 'Account Name', `${periodName} Amount`, 'Prior Period', 'Variance', 'Variance %'];
  const rows = lineItems.map((item) => [
    item.account_code,
    item.account_name,
    item.current_period.toString(),
    item.prior_period !== null ? item.prior_period.toString() : '',
    item.variance !== null ? item.variance.toString() : '',
    item.variance_pct !== null ? `${Number(item.variance_pct).toFixed(1)}%` : '',
  ]);

  const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title.replace(/\s+/g, '_')}_${periodName}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// --- Drill-Down Modal --------------------------------------------------------

interface DrillDownModalProps {
  lineItem: FinancialLineItem;
  periodId: string;
  /** When true, load per-entity slice from API (matches consolidation run entity-level results). */
  fetchFromApi: boolean;
  onClose: () => void;
}

function DrillDownModal({ lineItem, periodId, fetchFromApi, onClose }: DrillDownModalProps) {
  const [loading, setLoading] = useState(fetchFromApi);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [apiBreakdown, setApiBreakdown] = useState<
    Awaited<ReturnType<typeof reportsApi.drillDown>>['entity_breakdown'] | null
  >(null);

  useEffect(() => {
    if (!fetchFromApi || !periodId || !lineItem.account_code) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const data = await reportsApi.drillDown({
          period_id: periodId,
          account_code: lineItem.account_code,
        });
        if (!cancelled) setApiBreakdown(data.entity_breakdown ?? []);
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          (e instanceof Error ? e.message : 'Failed to load entity breakdown');
        if (!cancelled) {
          setFetchError(typeof msg === 'string' ? msg : 'Failed to load entity breakdown');
          setApiBreakdown([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFromApi, periodId, lineItem.account_code]);

  const items: DrillDownItem[] = useMemo(() => {
    if (fetchFromApi && apiBreakdown) {
      return apiBreakdown.map((row) => ({
        entity_id: row.entity_id,
        entity_name: row.entity_name,
        amount: Number(row.consolidated_balance),
        currency: row.currency || 'AED',
        pct_of_total: Number(row.pct_of_total),
      }));
    }
    const out: DrillDownItem[] = [];
    const totalAmount = Math.abs(lineItem.current_period) || 1;
    if (lineItem.entity_breakdown) {
      Object.entries(lineItem.entity_breakdown).forEach(([entityName, amount], idx) => {
        out.push({
          entity_id: idx + 1,
          entity_name: entityName,
          amount: amount as number,
          currency: 'AED',
          pct_of_total: totalAmount > 0 ? (Math.abs(amount as number) / totalAmount) * 100 : 0,
        });
      });
    }
    return out;
  }, [fetchFromApi, apiBreakdown, lineItem]);

  const pieData = items.map((item) => ({
    name: item.entity_name,
    value: Math.abs(item.amount),
  }));

  const totalDisplay = fetchFromApi && apiBreakdown != null
    ? items.reduce((s, i) => s + i.amount, 0)
    : lineItem.current_period;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Entity Breakdown</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {lineItem.account_code} - {lineItem.account_name}
            </p>
            {fetchFromApi && (
              <p className="text-xs text-gray-400 mt-1">
                Per latest completed consolidation run for this period (same scope as stored entity-level results).
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-500 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading entity breakdown…
            </div>
          )}
          {fetchError && !loading && (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">{fetchError}</div>
          )}
          {!loading && !fetchError && items.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">
              No entity-level rows for this account. Upload trial balances per entity and run consolidation so SKILL-008
              stores per-entity consolidation results.
            </p>
          )}
          {!loading && items.length > 0 && (
            <>
              {pieData.length > 0 && (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                      dataKey="value"
                      nameKey="name"
                      paddingAngle={2}
                      label={({ name, percent }: { name: string; percent: number }) =>
                        `${name} (${(percent * 100).toFixed(0)}%)`
                      }
                    >
                      {pieData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={DRILL_DOWN_COLORS[index % DRILL_DOWN_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                      formatter={(value: number) => [formatFinancialNumber(value), 'Amount']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}

              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="table-header">Entity</th>
                    <th className="table-header text-right w-32">Amount</th>
                    <th className="table-header text-right w-24">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={`${item.entity_id}-${idx}`} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="table-cell text-sm text-gray-700 flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: DRILL_DOWN_COLORS[idx % DRILL_DOWN_COLORS.length] }}
                        />
                        {item.entity_name}
                      </td>
                      <td
                        className={clsx(
                          'table-cell text-right font-mono text-sm',
                          item.amount < 0 ? 'text-financial-negative' : 'text-gray-900',
                        )}
                      >
                        {formatFinancialNumber(item.amount)}
                      </td>
                      <td className="table-cell text-right font-mono text-xs text-gray-500">
                        {Number(item.pct_of_total).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-300 bg-gray-50">
                    <td className="table-cell text-sm font-bold text-gray-900">Total</td>
                    <td
                      className={clsx(
                        'table-cell text-right font-mono text-sm font-bold',
                        totalDisplay < 0 ? 'text-financial-negative' : 'text-gray-900',
                      )}
                    >
                      {formatFinancialNumber(totalDisplay)}
                    </td>
                    <td className="table-cell text-right font-mono text-xs font-bold text-gray-700">100.0%</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Monthly Drill-Down Modal ------------------------------------------------

interface MonthlyDrillDownModalProps {
  lineItem: FinancialLineItem;
  entityId: string;
  periodId: string;
  onClose: () => void;
}

function MonthlyDrillDownModal({ lineItem, entityId, periodId, onClose }: MonthlyDrillDownModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    account_code: string;
    account_name: string;
    account_type: string;
    entity_name: string;
    target_period: string;
    currency: string;
    months: Array<{
      month: number;
      month_name: string;
      period_id: number;
      cumulative_ytd: number;
      monthly_amount: number;
    }>;
    ytd_total: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await reportsApi.monthlyDrilldown({
          entity_id: entityId,
          account_code: lineItem.account_code,
          period_id: periodId,
        });
        if (!cancelled) setData(result);
      } catch (err: unknown) {
        if (!cancelled) {
          const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
          setError(apiErr.response?.data?.detail || apiErr.message || 'Failed to load monthly breakdown');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [entityId, lineItem.account_code, periodId]);

  // Chart colors
  const BAR_COLOR = '#3b82f6';
  const BAR_NEGATIVE_COLOR = '#ef4444';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 overflow-hidden max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Monthly Breakdown</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {lineItem.account_code} &mdash; {lineItem.account_name}
              </p>
              {data && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {data.entity_name} &middot; {data.target_period} &middot; {data.currency}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-gray-500">Loading monthly data...</span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-100">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-800 font-medium">Failed to load breakdown</p>
                <p className="text-xs text-red-600 mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {data && data.months.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500 text-sm">
              No monthly trial balance data found for this account.
            </div>
          )}

          {data && data.months.length > 0 && (
            <>
              {/* Bar Chart */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Monthly Amounts
                </h4>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.months} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="month_name"
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: number) => {
                        if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
                        if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
                        return v.toString();
                      }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                      formatter={(value: number) => [formatFinancialNumber(value), 'Monthly Amount']}
                      labelFormatter={(label: string) => `Month: ${label}`}
                    />
                    <Bar
                      dataKey="monthly_amount"
                      name="Monthly Amount"
                      radius={[4, 4, 0, 0]}
                    >
                      {data.months.map((entry, index) => (
                        <Cell
                          key={`bar-${index}`}
                          fill={entry.monthly_amount < 0 ? BAR_NEGATIVE_COLOR : BAR_COLOR}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Data Table */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Month-by-Month Detail
                </h4>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="table-header">Month</th>
                      <th className="table-header text-right w-36">Monthly Amount</th>
                      <th className="table-header text-right w-36">Cumulative YTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.map((m, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="table-cell text-sm text-gray-700 font-medium">
                          {m.month_name} {data.target_period.split('-')[0]}
                        </td>
                        <td className={clsx(
                          'table-cell text-right font-mono text-sm',
                          m.monthly_amount < 0 ? 'text-financial-negative' : 'text-gray-900',
                        )}>
                          {formatFinancialNumber(m.monthly_amount)}
                        </td>
                        <td className="table-cell text-right font-mono text-sm text-gray-500">
                          {formatFinancialNumber(m.cumulative_ytd)}
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr className="border-t-2 border-gray-300 bg-gray-50">
                      <td className="table-cell text-sm font-bold text-gray-900">YTD Total</td>
                      <td className={clsx(
                        'table-cell text-right font-mono text-sm font-bold',
                        data.ytd_total < 0 ? 'text-financial-negative' : 'text-gray-900',
                      )}>
                        {formatFinancialNumber(data.ytd_total)}
                      </td>
                      <td className={clsx(
                        'table-cell text-right font-mono text-sm font-bold',
                        data.ytd_total < 0 ? 'text-financial-negative' : 'text-gray-700',
                      )}>
                        {formatFinancialNumber(data.ytd_total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Equity Changes Table ----------------------------------------------------

interface EquityChangesProps {
  periodName: string;
}

function EquityChangesTable({ periodName }: EquityChangesProps) {
  const columns = ['Share Capital', 'Retained Earnings', 'OCI Reserve', 'NCI', 'Total Equity'];
  const rows = [
    { label: 'Opening Balance', values: [null, null, null, null, null] as (number | null)[] },
    { label: 'Profit for Period', values: [null, null, null, null, null] as (number | null)[] },
    { label: 'Other Comprehensive Income', values: [null, null, null, null, null] as (number | null)[] },
    { label: 'Dividends', values: [null, null, null, null, null] as (number | null)[] },
    { label: 'NCI Changes', values: [null, null, null, null, null] as (number | null)[] },
    { label: 'Closing Balance', values: [null, null, null, null, null] as (number | null)[] },
  ];

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
        <div className="text-center">
          <h2 className="text-base font-bold text-gray-900">Statement of Changes in Equity</h2>
          <p className="text-xs text-gray-500 mt-1">For the period ending {periodName}</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="table-header w-48"></th>
              {columns.map((col) => (
                <th key={col} className="table-header text-right w-32">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ridx) => {
              const isTotal = row.label === 'Closing Balance';
              return (
                <tr
                  key={ridx}
                  className={clsx(
                    'border-b border-gray-100',
                    isTotal && 'border-t-2 border-gray-300 bg-gray-50',
                  )}
                >
                  <td className={clsx(
                    'table-cell text-sm',
                    isTotal ? 'font-bold text-gray-900' : 'text-gray-700',
                  )}>
                    {row.label}
                  </td>
                  {row.values.map((val, cidx) => (
                    <td
                      key={cidx}
                      className={clsx(
                        'table-cell text-right font-mono text-sm',
                        isTotal ? 'font-bold text-gray-900' : 'text-gray-600',
                      )}
                    >
                      {val !== null ? formatFinancialNumber(val) : '\u2014'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 text-xs text-gray-400 text-center">
        Data will populate after consolidation run is approved.
      </div>
    </div>
  );
}

// --- Financial Statement Table -----------------------------------------------

interface StatementTableProps {
  lineItems: FinancialLineItem[];
  showComparative: boolean;
  periodName: string;
  priorPeriodName: string;
  /** Consolidated view: opens API drill-down by account. Inline `entity_breakdown` also triggers when present. */
  onDrillDown?: (item: FinancialLineItem) => void;
  onMonthlyDrillDown?: (item: FinancialLineItem) => void;
  consolidatedDrillDown?: boolean;
}

function StatementTable({
  lineItems,
  showComparative,
  periodName,
  priorPeriodName,
  onDrillDown,
  onMonthlyDrillDown,
  consolidatedDrillDown = false,
}: StatementTableProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="table-header w-20">Code</th>
            <th className="table-header">Account</th>
            <th className="table-header text-right w-36">{periodName}</th>
            {showComparative && (
              <>
                <th className="table-header text-right w-36">{priorPeriodName}</th>
                <th className="table-header text-right w-28">Variance</th>
                <th className="table-header text-right w-20">%</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, index) => {
            const rowKey = `${item.account_code}-${item.account_name}-${index}`;
            const isExpanded = expandedRow === rowKey;
            const hasBreakdown = item.entity_breakdown && Object.keys(item.entity_breakdown).length > 0;
            const showEntityDrillButton =
              !!onDrillDown &&
              !item.is_header &&
              !item.is_total &&
              !!String(item.account_code ?? '').trim() &&
              (hasBreakdown || consolidatedDrillDown);
            const isNegative = item.current_period < 0;
            const variancePositive = item.variance !== null && item.variance > 0;
            const varianceNegative = item.variance !== null && item.variance < 0;

            return (
              <Fragment key={rowKey}>
                <tr
                  className={clsx(
                    'transition-colors',
                    item.is_header && 'bg-gray-50/80',
                    item.is_total && 'border-t border-gray-200',
                    !item.is_header && !item.is_total && 'hover:bg-gray-50/50',
                    hasBreakdown && 'cursor-pointer',
                  )}
                  onClick={() => hasBreakdown && setExpandedRow(isExpanded ? null : rowKey)}
                >
                  <td className="table-cell font-mono text-xs text-gray-400">
                    {item.account_code}
                  </td>
                  <td className="table-cell">
                    <div
                      className="flex items-center gap-1"
                      style={{ paddingLeft: `${item.indent_level * 20}px` }}
                    >
                      {hasBreakdown && (
                        isExpanded
                          ? <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          : <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      )}
                      <span
                        className={clsx(
                          item.is_header && 'text-xs font-bold text-gray-500 uppercase tracking-wider',
                          item.is_total && item.indent_level === 0 && 'text-sm font-bold text-gray-900',
                          item.is_total && item.indent_level > 0 && 'text-sm font-semibold text-gray-800',
                          !item.is_header && !item.is_total && 'text-sm text-gray-700',
                        )}
                      >
                        {item.account_name}
                      </span>
                    </div>
                  </td>
                  <td
                    className={clsx(
                      'table-cell text-right font-mono text-sm',
                      item.is_header && 'text-transparent',
                      item.is_total && 'font-bold',
                      isNegative && !item.is_header && 'text-financial-negative',
                      !isNegative && !item.is_header && 'text-gray-900',
                    )}
                  >
                    {item.is_header ? (
                      '\u2014'
                    ) : showEntityDrillButton ? (
                      <button
                        type="button"
                        className="underline decoration-dotted underline-offset-2 hover:text-primary-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDrillDown(item);
                        }}
                        title={consolidatedDrillDown ? 'Entity breakdown (consolidation run)' : 'Entity breakdown'}
                      >
                        {formatFinancialNumber(item.current_period)}
                      </button>
                    ) : onMonthlyDrillDown && !item.is_header && !item.is_total && item.current_period !== 0 ? (
                      <button
                        className="underline decoration-dotted underline-offset-2 hover:text-blue-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMonthlyDrillDown(item);
                        }}
                        title="View monthly breakdown"
                      >
                        {formatFinancialNumber(item.current_period)}
                      </button>
                    ) : (
                      formatFinancialNumber(item.current_period)
                    )}
                  </td>
                  {showComparative && (
                    <>
                      <td
                        className={clsx(
                          'table-cell text-right font-mono text-sm',
                          item.is_header && 'text-transparent',
                          (item.prior_period ?? 0) < 0 && 'text-financial-negative',
                        )}
                      >
                        {item.prior_period !== null ? formatFinancialNumber(item.prior_period) : '\u2014'}
                      </td>
                      <td
                        className={clsx(
                          'table-cell text-right font-mono text-xs',
                          variancePositive && 'text-financial-positive',
                          varianceNegative && 'text-financial-negative',
                        )}
                      >
                        {item.variance !== null ? formatFinancialNumber(item.variance, true) : '\u2014'}
                      </td>
                      <td
                        className={clsx(
                          'table-cell text-right font-mono text-xs',
                          variancePositive && 'text-financial-positive',
                          varianceNegative && 'text-financial-negative',
                        )}
                      >
                        {item.variance_pct !== null
                          ? `${Number(item.variance_pct) > 0 ? '+' : ''}${Number(item.variance_pct).toFixed(1)}%`
                          : '\u2014'}
                      </td>
                    </>
                  )}
                </tr>
                {/* Entity breakdown drill-down */}
                {isExpanded && item.entity_breakdown && (
                  Object.entries(item.entity_breakdown).map(([entity, amount]) => (
                    <tr key={`${rowKey}-${entity}`} className="bg-blue-50/30">
                      <td className="table-cell"></td>
                      <td className="table-cell">
                        <span
                          className="text-xs text-blue-600 italic"
                          style={{ paddingLeft: `${(item.indent_level + 1) * 20 + 16}px` }}
                        >
                          {entity}
                        </span>
                      </td>
                      <td className="table-cell text-right font-mono text-xs text-blue-700">
                        {formatFinancialNumber(amount)}
                      </td>
                      {showComparative && (
                        <>
                          <td className="table-cell text-right text-xs text-gray-300">&mdash;</td>
                          <td className="table-cell"></td>
                          <td className="table-cell"></td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- NCI Report View ---------------------------------------------------------

function NciReportView({ data }: { data: NciReport }) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 bg-white border-l-4 border-blue-500">
          <p className="text-xs font-medium text-gray-500 uppercase">Ownership Structure</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{data.ownership_pct}%</span>
            <span className="text-sm text-gray-500">Parent</span>
          </div>
          <div className="mt-1 text-sm font-medium text-blue-600">
            {data.nci_pct}% Minority (NCI)
          </div>
        </div>

        <div className="card p-4 bg-white">
          <p className="text-xs font-medium text-gray-500 uppercase">P&L NCI Share</p>
          <div className="mt-2 text-2xl font-bold text-gray-900">
            {formatFinancialNumber(data.pnl_nci)}
          </div>
          <p className="mt-1 text-xs text-gray-400">Total P&L split by {data.nci_pct}%</p>
        </div>

        <div className="card p-4 bg-white">
          <p className="text-xs font-medium text-gray-500 uppercase">Equity NCI Share</p>
          <div className="mt-2 text-2xl font-bold text-gray-900">
            {formatFinancialNumber(data.equity_nci)}
          </div>
          <p className="mt-1 text-xs text-gray-400">Base equity components</p>
        </div>

        <div className="card p-4 bg-primary-50 border border-primary-100">
          <p className="text-xs font-medium text-primary-600 uppercase">Total NCI Amount</p>
          <div className="mt-2 text-2xl font-bold text-primary-900">
            {formatFinancialNumber(data.total_nci)}
          </div>
          <p className="mt-1 text-xs text-primary-400">P&L + Equity + CTA</p>
        </div>
      </div>

      {/* Detail Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Calculation Breakout</h3>
          <span className="text-xs text-gray-500">Currency: {data.currency}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="table-header w-24">Code</th>
                <th className="table-header">Account Name</th>
                <th className="table-header w-24">Type</th>
                <th className="table-header text-right w-36">Total (100%)</th>
                <th className="table-header text-right w-36">Parent ({data.ownership_pct}%)</th>
                <th className="table-header text-right w-36 font-bold text-primary-700 bg-primary-50/30">NCI ({data.nci_pct}%)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.breakdown.map((row: NciReportRow, idx: number) => (
                <tr key={`${row.account_code}-${idx}`} className="hover:bg-gray-50/50 transition-colors">
                  <td className="table-cell font-mono text-xs text-gray-400">{row.account_code}</td>
                  <td className="table-cell text-sm text-gray-700 font-medium">{row.account_name}</td>
                  <td className="table-cell">
                    <span className={clsx(
                      "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider",
                      row.account_type === 'revenue' || row.account_type === 'expense' ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-600'
                    )}>
                      {row.account_type}
                    </span>
                  </td>
                  <td className="table-cell text-right font-mono text-sm text-gray-500">
                    {formatFinancialNumber(row.total_balance)}
                  </td>
                  <td className="table-cell text-right font-mono text-sm text-gray-600">
                    {formatFinancialNumber(row.parent_portion)}
                  </td>
                  <td className="table-cell text-right font-mono text-sm font-bold text-primary-900 bg-primary-50/10">
                    {formatFinancialNumber(row.nci_portion)}
                  </td>
                </tr>
              ))}
              {data.breakdown.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-12 text-gray-500">
                    No data found for this entity and period. Please ensure trial balance is uploaded and translated.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr>
                <td colSpan={3} className="table-cell font-bold text-gray-900">Total Reported NCI</td>
                <td className="table-cell text-right font-mono text-sm font-bold text-gray-400 italic">Reference only</td>
                <td className="table-cell text-right font-mono text-sm font-bold text-gray-600">
                  {/* Optional: Sum parent portion */}
                </td>
                <td className="table-cell text-right font-mono text-sm font-bold text-primary-700">
                  {formatFinancialNumber(data.total_nci)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// --- NCI Pipeline Panel -------------------------------------------------------

interface NciPipelinePanelProps {
  selectedEntity: string;
  selectedPeriod: string;
  entities: Pick<{ id: string; name: string }, 'id' | 'name'>[];
  nciCalculating: boolean;
  nciResult: NciPipelineResult | null;
  nciError: string | null;
  nciLog: NciPipelineStep[];
  onCalculate: () => void;
}

function NciPipelinePanel({
  selectedEntity,
  selectedPeriod,
  entities,
  nciCalculating,
  nciResult,
  nciError,
  nciLog,
  onCalculate,
}: NciPipelinePanelProps) {
  const entityName = entities.find((e) => e.id === selectedEntity)?.name ?? '';
  const canCalculate = selectedEntity !== 'consolidated' && !!selectedPeriod && !nciCalculating;

  const stepIcon = (s: NciPipelineStep) => {
    if (s.status === 'completed') return <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />;
    if (s.status === 'skipped') return <SkipForward className="w-4 h-4 text-amber-400 flex-shrink-0" />;
    if (s.status === 'warning') return <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />;
    return <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />;
  };

  const stepRowCls = (s: NciPipelineStep) =>
    clsx(
      'flex items-start gap-3 px-4 py-2 rounded-lg text-sm',
      s.status === 'completed' && 'bg-emerald-50 text-emerald-800',
      s.status === 'skipped' && 'bg-amber-50 text-amber-700',
      s.status === 'warning' && 'bg-amber-50 text-amber-700',
      s.status === 'failed' && 'bg-red-50 text-red-700',
    );

  return (
    <div className="space-y-6">
      {/* Header card — action panel */}
      <div className="card p-6 bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-primary-600" />
              Non-Controlling Interest (NCI) Calculation
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {selectedEntity === 'consolidated'
                ? 'Select a specific entity to run NCI calculation.'
                : entityName
                  ? `Entity: ${entityName} · Reporting currency: AED`
                  : 'Select an entity and period, then click Calculate NCI.'}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Single click executes: Translation check → CTA → P&amp;L NCI → Equity NCI → Total NCI
            </p>
          </div>
          <button
            id="btn-calculate-nci"
            onClick={onCalculate}
            disabled={!canCalculate}
            className={clsx(
              'flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-sm',
              canCalculate
                ? 'bg-primary-600 text-white hover:bg-primary-700 active:scale-95'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed',
            )}
          >
            {nciCalculating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</>
            ) : nciResult ? (
              <><RefreshCw className="w-4 h-4" /> Recalculate NCI</>
            ) : (
              <><PlayCircle className="w-4 h-4" /> Calculate NCI</>
            )}
          </button>
        </div>
      </div>

      {/* Validation guard */}
      {selectedEntity === 'consolidated' && (
        <div className="card p-6 text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <p className="text-sm font-medium text-gray-700">Please select a specific entity</p>
          <p className="text-xs text-gray-400 mt-1">NCI calculation requires a single entity with non-100% ownership.</p>
        </div>
      )}

      {/* Error */}
      {nciError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">Calculation Failed</p>
            <p className="text-sm text-red-700 mt-0.5">{nciError}</p>
          </div>
        </div>
      )}

      {/* Execution step log */}
      {(nciCalculating || nciLog.length > 0) && (
        <div className="card overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Execution Log</span>
            {nciResult && (
              <span className="ml-auto text-xs text-gray-400">{nciResult.total_duration_ms} ms total</span>
            )}
          </div>
          <div className="p-4 space-y-2">
            {nciCalculating && nciLog.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-500 animate-pulse px-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Running pipeline…
              </div>
            )}
            {nciLog.map((step, idx) => (
              <div key={idx} className={stepRowCls(step)}>
                {stepIcon(step)}
                <div className="flex-1 min-w-0">
                  <span className="font-semibold">{step.step}</span>
                  <span className="mx-2 text-gray-300">·</span>
                  <span className="text-xs opacity-80 break-words">{step.message}</span>
                </div>
                <span className="text-xs opacity-60 flex-shrink-0">{step.duration_ms} ms</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 100%-owned — skipped state */}
      {nciResult?.status === 'skipped' && (
        <div className="card p-8 text-center">
          <SkipForward className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <p className="text-base font-semibold text-gray-700">{nciResult.message}</p>
          <p className="text-sm text-gray-400 mt-1">NCI calculation is only applicable for entities with less than 100% parent ownership.</p>
        </div>
      )}

      {/* Results */}
      {nciResult?.status === 'success' && (
        <>
          {/* Translation source badge */}
          <div className="flex items-center gap-2">
            <span className={clsx(
              'px-2.5 py-1 rounded-full text-xs font-semibold',
              nciResult.translation_source === 'existing'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-blue-50 text-blue-700 border border-blue-200',
            )}>
              {nciResult.translation_source === 'existing'
                ? '✓ Used cached translation data'
                : '⚡ Translation computed in real-time'}
            </span>
            {nciResult.cta_computed && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                CTA = {formatFinancialNumber(nciResult.cta_amount ?? 0)} {nciResult.currency}
              </span>
            )}
            {nciResult.validation_ok != null && (
              <span className={clsx(
                'px-2.5 py-1 rounded-full text-xs font-semibold border',
                nciResult.validation_ok
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200',
              )}>
                {nciResult.validation_ok ? '✓ Total = Parent + NCI' : '⚠ Rounding variance detected'}
              </span>
            )}
          </div>

          {/* Summary cards — reuse NciReportView layout */}
          <NciReportView data={nciResult} />
        </>
      )}

      {/* Empty state — no calculation run yet */}
      {!nciCalculating && !nciResult && !nciError && selectedEntity !== 'consolidated' && (
        <div className="card py-16 text-center">
          <PlayCircle className="w-12 h-12 mx-auto text-gray-200 mb-3" />
          <p className="text-base font-medium text-gray-700">Ready to calculate</p>
          <p className="text-sm text-gray-400 mt-1">
            Click <strong>Calculate NCI</strong> to automatically run the full NCI computation pipeline.
          </p>
          <p className="text-xs text-gray-300 mt-2">
            Translation → CTA → P&amp;L NCI → Equity NCI → Total NCI
          </p>
        </div>
      )}
    </div>
  );
}

// --- Reports Page ------------------------------------------------------------

type TabType = 'balance_sheet' | 'income_statement' | 'cash_flow' | 'equity_changes' | 'nci' | 'consolidation';

export default function Reports() {
  const { addNotification } = useAppStore();

  const [activeTab, setActiveTab] = useState<TabType>('balance_sheet');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedEntity, setSelectedEntity] = useState('consolidated');
  const [showComparative, setShowComparative] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  // NCI pipeline state
  const [nciCalculating, setNciCalculating] = useState(false);
  const [nciResult, setNciResult] = useState<NciPipelineResult | null>(null);
  const [nciError, setNciError] = useState<string | null>(null);
  const [nciLog, setNciLog] = useState<NciPipelineStep[]>([]);


  // Data from API
  const [periods, setPeriods] = useState<Period[]>([]);
  const [entities, setEntities] = useState<Pick<Entity, 'id' | 'name' | 'code'>[]>([]);
  const [statement, setStatement] = useState<FinancialStatement | null>(null);


  // Reset NCI state when entity or period changes
  useEffect(() => {
    setNciResult(null);
    setNciError(null);
    setNciLog([]);
  }, [selectedEntity, selectedPeriod]);


  // Drill-down modals
  const [drillDownItem, setDrillDownItem] = useState<FinancialLineItem | null>(null);
  const [monthlyDrillDownItem, setMonthlyDrillDownItem] = useState<FinancialLineItem | null>(null);

  // Load periods and entities on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [periodsRes, entitiesRes] = await Promise.all([
          periodsApi.list(),
          entitiesApi.list({ page: 1, page_size: 200 }),
        ]);
        setPeriods(periodsRes);
        const entityList: Pick<Entity, 'id' | 'name' | 'code'>[] = [
          { id: 'consolidated', name: 'Consolidated (All Entities)', code: '' },
          ...entitiesRes.items.map((e) => ({ id: e.id, name: e.name, code: e.code })),
        ];
        setEntities(entityList);

        // Auto-select most recent period
        const openPeriod = periodsRes.find((p) => p.status === 'open') || periodsRes[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // Silent fail
      }
    };
    loadFilters();
  }, []);

  // Load statement data when tab/period/entity changes
  const loadStatement = useCallback(async () => {
    if (!selectedPeriod || activeTab === 'equity_changes' || activeTab === 'consolidation') {
      setIsLoading(false);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);

    const params = {
      period_id: selectedPeriod,
      entity_id: selectedEntity !== 'consolidated' ? selectedEntity : undefined,
      comparative: showComparative,
    };

    try {
      let result: FinancialStatement;
      switch (activeTab) {
        case 'balance_sheet':
          result = await reportsApi.balanceSheet(params);
          break;
        case 'income_statement':
          result = await reportsApi.incomeStatement(params);
          break;
        case 'cash_flow':
          result = await reportsApi.cashFlow(params);
          break;
        case 'nci':
          // NCI tab is manual-only — user must click "Calculate NCI"
          setIsLoading(false);
          return;
        default:
          setIsLoading(false);
          return;
      }
      setStatement(result);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load financial statement');
      setStatement(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod, selectedEntity, activeTab, showComparative]);

  useEffect(() => {
    if (selectedPeriod) {
      loadStatement();
    }
  }, [loadStatement, selectedPeriod, selectedEntity, activeTab]);

  const tabs: { id: TabType; label: string }[] = [
    { id: 'balance_sheet', label: 'Balance Sheet' },
    { id: 'income_statement', label: 'Income Statement' },
    { id: 'cash_flow', label: 'Cash Flow' },
    { id: 'equity_changes', label: 'Equity Changes' },
    { id: 'consolidation', label: 'Consolidation' },
    { id: 'nci', label: 'NCI' },
  ];

  // Export XLSX via exportsApi
  const handleExportXlsx = async () => {
    if (!selectedPeriod) return;
    setExporting('xlsx');
    try {
      const exportData = {
        report_type: activeTab,
        period_id: Number(selectedPeriod),
        entity_id: selectedEntity !== 'consolidated' ? Number(selectedEntity) : undefined,
        format: 'xlsx' as const,
        include_comparative: showComparative,
      };
      let blob: Blob;
      switch (activeTab) {
        case 'balance_sheet':
          blob = await exportsApi.balanceSheet(exportData);
          break;
        case 'income_statement':
          blob = await exportsApi.incomeStatement(exportData);
          break;
        case 'cash_flow':
          blob = await exportsApi.cashFlow(exportData);
          break;
        default:
          setExporting(null);
          return;
      }
      const periodName = periods.find((p) => p.id === selectedPeriod)?.name || selectedPeriod;
      const tabLabel = activeTab.replace(/_/g, '_');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tabLabel}_${periodName}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      addNotification({ type: 'success', title: 'Export Complete', message: 'XLSX file has been downloaded.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      // Fallback to CSV
      if (statement) {
        exportToCSV(statement.line_items, activeTab.replace('_', ' '), statement.period_name);
        addNotification({ type: 'success', title: 'Export Complete', message: 'CSV file has been downloaded (XLSX not available).' });
      } else {
        addNotification({ type: 'error', title: 'Export Failed', message: apiErr.detail || 'Failed to export XLSX.' });
      }
    } finally {
      setExporting(null);
    }
  };

  // Export PDF via exportsApi
  const handleExportPdf = async () => {
    if (!selectedPeriod) return;
    setExporting('pdf');
    try {
      const exportData = {
        report_type: activeTab,
        period_id: Number(selectedPeriod),
        entity_id: selectedEntity !== 'consolidated' ? Number(selectedEntity) : undefined,
        format: 'pdf' as const,
        include_comparative: showComparative,
      };
      let blob: Blob;
      switch (activeTab) {
        case 'balance_sheet':
          blob = await exportsApi.balanceSheet(exportData);
          break;
        case 'income_statement':
          blob = await exportsApi.incomeStatement(exportData);
          break;
        case 'cash_flow':
          blob = await exportsApi.cashFlow(exportData);
          break;
        default:
          setExporting(null);
          return;
      }
      const periodName = periods.find((p) => p.id === selectedPeriod)?.name || selectedPeriod;
      const tabLabel = activeTab.replace(/_/g, '_');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tabLabel}_${periodName}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      addNotification({ type: 'success', title: 'Export Complete', message: 'PDF file has been downloaded.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Export Failed', message: apiErr.detail || 'Failed to export PDF.' });
    } finally {
      setExporting(null);
    }
  };

  const currentPeriod = periods.find((p) => p.id === selectedPeriod);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Consolidated financial statements and analytics</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab !== 'consolidation' && (
            <>
              <button
                onClick={handleExportXlsx}
                disabled={exporting !== null || !statement}
                className="btn-secondary"
              >
                {exporting === 'xlsx' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                )}
                Export XLSX
              </button>
              <button
                onClick={handleExportPdf}
                disabled={exporting !== null || !statement}
                className="btn-secondary"
              >
                {exporting === 'pdf' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Export PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-6 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'pb-3 text-sm font-medium border-b-2 transition-colors -mb-px',
              activeTab === tab.id
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-500 uppercase">Period</label>
          <select
            className="select-field text-sm w-36"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">Loading...</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 min-w-[420px]">
          <label className="text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Entity</label>
          <div className="flex-1">
            <SearchableSelect
              options={entities.map((e) => ({ value: String(e.id), label: e.name, secondary: e.code }))}
              value={selectedEntity}
              onChange={setSelectedEntity}
              placeholder="Select entity..."
            />
          </div>
        </div>
        {activeTab !== 'consolidation' && (
          <label className="flex items-center gap-2 ml-auto">
            <input
              type="checkbox"
              checked={showComparative}
              onChange={(e) => setShowComparative(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-600">Show comparative period</span>
          </label>
        )}
      </div>

      {/* Statement */}
      {activeTab === 'consolidation' ? (
        <ConsolidationReviewPanel
          periodId={selectedPeriod || null}
          scopeEntityId={selectedEntity !== 'consolidated' ? selectedEntity : undefined}
        />
      ) : isLoading ? (
        <div className="card p-6">
          <div className="space-y-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="skeleton w-16 h-4" />
                <div className="skeleton flex-1 h-4" />
                <div className="skeleton w-24 h-4" />
                {showComparative && (
                  <>
                    <div className="skeleton w-24 h-4" />
                    <div className="skeleton w-20 h-4" />
                    <div className="skeleton w-12 h-4" />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="card p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">Failed to load report</h3>
          <p className="text-sm text-red-600 mt-1">{error}</p>
          <button onClick={loadStatement} className="btn-primary mt-4">Retry</button>
        </div>
      ) : activeTab === 'equity_changes' ? (
        <EquityChangesTable periodName={currentPeriod?.name || ''} />
      ) : activeTab === 'nci' ? (
        <NciPipelinePanel
          selectedEntity={selectedEntity}
          selectedPeriod={selectedPeriod}
          entities={entities}
          nciCalculating={nciCalculating}
          nciResult={nciResult}
          nciError={nciError}
          nciLog={nciLog}
          onCalculate={async () => {
            if (!selectedPeriod || selectedEntity === 'consolidated') return;
            setNciCalculating(true);
            setNciError(null);
            setNciLog([]);
            setNciResult(null);
            try {
              const res = await trialBalancesApi.nciPipeline({
                entity_id: selectedEntity,
                period_id: selectedPeriod,
                reporting_currency: 'AED',
              });
              setNciResult(res);
              setNciLog(res.steps_executed ?? []);
              if (res.status === 'success') {
                addNotification({ type: 'success', title: 'NCI Calculated', message: `Total NCI: ${res.total_nci.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${res.currency}` });
              }
            } catch (err: unknown) {
              const apiErr = err as { response?: { data?: { detail?: string } }; message?: string };
              const msg = apiErr?.response?.data?.detail ?? (err instanceof Error ? err.message : 'NCI calculation failed');
              setNciError(typeof msg === 'string' ? msg : 'NCI calculation failed');
              addNotification({ type: 'error', title: 'NCI Failed', message: typeof msg === 'string' ? msg : 'NCI calculation failed' });
            } finally {
              setNciCalculating(false);
            }
          }}
        />
      ) : statement ? (
        <>
          {/* Balance Sheet Imbalance Analysis */}
          {activeTab === 'balance_sheet' && (() => {
            const totalAssets = statement.totals.total_assets ?? 0;
            const totalLiab = statement.totals.total_liabilities ?? 0;
            const totalEquity = statement.totals.total_equity ?? 0;
            const totalEqAndLiab = totalLiab + totalEquity;
            const difference = Math.round((totalAssets + totalEqAndLiab) * 100) / 100;
            const isUnbalanced = Math.abs(difference) > 1.00;
            const unmapped = statement.raw_unmapped_accounts ?? [];
            const unmappedTotal = statement.unmapped_total ?? 0;

            if (!isUnbalanced) return null;

            return (
              <div className="rounded-lg border border-red-200 bg-red-50 p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 space-y-4">
                    <h4 className="text-sm font-bold text-red-800">Balance Sheet Analysis — Out of Balance</h4>

                    {/* Summary table */}
                    <div className="bg-white/70 rounded-lg border border-red-100 overflow-hidden">
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-red-100">
                            <td className="py-2 px-4 text-gray-700 font-medium">Total Assets</td>
                            <td className="py-2 px-4 text-right font-mono text-gray-900">{formatFinancialNumber(totalAssets)}</td>
                          </tr>
                          <tr className="border-b border-red-100">
                            <td className="py-2 px-4 text-gray-700 font-medium">Total Equity & Liabilities</td>
                            <td className="py-2 px-4 text-right font-mono text-gray-900">{formatFinancialNumber(Math.abs(totalEqAndLiab))}</td>
                          </tr>
                          <tr className="bg-red-100/50">
                            <td className="py-2 px-4 text-red-800 font-bold">Difference</td>
                            <td className="py-2 px-4 text-right font-mono font-bold text-red-700">{formatFinancialNumber(difference)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Root cause — unmapped accounts */}
                    {unmapped.length > 0 && (
                      <>
                        <div className="text-sm text-red-700">
                          <span className="font-semibold">Root Cause:</span>{' '}
                          {unmapped.length} unmapped account{unmapped.length > 1 ? 's' : ''} with total balance of{' '}
                          <span className="font-mono font-semibold">{formatFinancialNumber(unmappedTotal)}</span>.
                          These accounts have no Group Account mapping and cannot be classified into Assets, Liabilities, or Equity.
                        </div>

                        <div className="bg-white/70 rounded-lg border border-red-100 overflow-hidden">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-red-200 bg-red-50/50">
                                <th className="py-2 px-4 text-left text-xs font-semibold text-red-700 uppercase tracking-wider">Code</th>
                                <th className="py-2 px-4 text-left text-xs font-semibold text-red-700 uppercase tracking-wider">Account Name</th>
                                <th className="py-2 px-4 text-right text-xs font-semibold text-red-700 uppercase tracking-wider">Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {unmapped.map((item, idx) => {
                                const bal = Number(item.consolidated_balance || 0);
                                return (
                                  <tr key={idx} className="border-b border-red-50 hover:bg-red-50/30">
                                    <td className="py-1.5 px-4 font-mono text-xs text-gray-500">{item.account_code}</td>
                                    <td className="py-1.5 px-4 text-gray-700">{item.account_name}</td>
                                    <td className={`py-1.5 px-4 text-right font-mono ${bal < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                      {formatFinancialNumber(bal)}
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr className="bg-red-100/30 border-t border-red-200">
                                <td className="py-2 px-4" />
                                <td className="py-2 px-4 font-semibold text-gray-800">Total Unmapped</td>
                                <td className="py-2 px-4 text-right font-mono font-bold text-red-700">{formatFinancialNumber(unmappedTotal)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                    {/* Action guidance */}
                    <div className="flex items-start gap-2 bg-white/50 rounded-lg border border-red-100 p-3">
                      <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-red-700 space-y-1">
                        <p className="font-semibold">How to fix:</p>
                        <p>Go to <strong>Entity COA</strong> and map the unmapped accounts to group accounts using <strong>AI Map</strong> (automatic) or the pencil icon (manual).</p>
                        <p>Once all accounts are mapped, the Balance Sheet will balance automatically.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="card overflow-hidden">
            {/* Report header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <div className="text-center">
                <h2 className="text-base font-bold text-gray-900">
                  {statement.entity_name || (selectedEntity === 'consolidated' ? 'Consolidated' : entities.find((e) => e.id === selectedEntity)?.name)}
                </h2>
                <h3 className="text-sm font-semibold text-gray-700 mt-0.5">
                  {activeTab === 'balance_sheet' && (selectedEntity === 'consolidated' ? 'Consolidated Balance Sheet' : 'Balance Sheet')}
                  {activeTab === 'income_statement' && (selectedEntity === 'consolidated' ? 'Consolidated Income Statement' : 'Income Statement')}
                  {activeTab === 'cash_flow' && (selectedEntity === 'consolidated' ? 'Consolidated Cash Flow Statement' : 'Cash Flow Statement')}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const endDate = currentPeriod?.end_date
                      ? new Date(currentPeriod.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                      : '';
                    return activeTab === 'balance_sheet'
                      ? `As at ${endDate}`
                      : `For the period ended ${endDate}`;
                  })()}
                  {' '}&middot; Currency: {statement.currency || 'AED'}
                </p>
              </div>
            </div>

            <StatementTable
              lineItems={statement.line_items || []}
              showComparative={showComparative}
              periodName={statement.period_name || currentPeriod?.name || 'Current'}
              priorPeriodName={statement.prior_period_name || 'Prior Period'}
              onDrillDown={selectedEntity === 'consolidated' ? setDrillDownItem : undefined}
              consolidatedDrillDown={selectedEntity === 'consolidated'}
              onMonthlyDrillDown={selectedEntity !== 'consolidated' ? setMonthlyDrillDownItem : undefined}
            />

            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 text-xs text-gray-400 text-center">
              Generated by ConsolidateAI &middot;{' '}
              {selectedEntity !== 'consolidated'
                ? 'Click on amounts to view monthly breakdown'
                : 'Click on amounts to drill down by entity'}
            </div>
          </div>
        </>
      ) : (
        <div className="card py-16 text-center">
          <FileBarChart className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No report data available</h3>
          <p className="text-sm text-gray-500 mt-1">
            Run a consolidation first to generate financial statements.
          </p>
        </div>
      )}

      {/* Drill-Down Modal (Entity Breakdown for consolidated view) */}
      {drillDownItem && (
        <DrillDownModal
          lineItem={drillDownItem}
          periodId={selectedPeriod}
          fetchFromApi={selectedEntity === 'consolidated'}
          onClose={() => setDrillDownItem(null)}
        />
      )}

      {/* Monthly Drill-Down Modal (Month-by-month breakdown for entity view) */}
      {monthlyDrillDownItem && selectedEntity !== 'consolidated' && (
        <MonthlyDrillDownModal
          lineItem={monthlyDrillDownItem}
          entityId={selectedEntity}
          periodId={selectedPeriod}
          onClose={() => setMonthlyDrillDownItem(null)}
        />
      )}
    </div>
  );
}
